import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ProductGrid } from './ProductGrid';
import { Typography, CircularProgress, Box, Card, Grid } from '@mui/material';
import { getCategories } from '../firebase/categories';
import { Category } from '../types';

const CategoryPage = () => {
  const { type } = useParams<{ type: string }>();
  const [mainCategory, setMainCategory] = useState<Category | null>(null);
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCategories();
  }, [type]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const categories = await getCategories();
      
      // Find the main category by name
      const foundMainCategory = categories.find(
        cat => cat.name.toLowerCase() === type?.toLowerCase()
      );
      
      if (foundMainCategory) {
        setMainCategory(foundMainCategory);
        // Find subcategories
        const foundSubCategories = categories.filter(
          cat => cat.parentId === foundMainCategory.id
        );
        setSubCategories(foundSubCategories);
        // Set main category as selected by default
        setSelectedCategory(foundMainCategory.id);
      } else {
        setError('No se encontró la categoría');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box className="py-8 flex justify-center">
        <CircularProgress sx={{ color: '#00ff00' }} />
      </Box>
    );
  }

  if (error || !mainCategory) {
    return (
      <Box className="py-8">
        <Typography 
          variant="h5" 
          className="text-center"
          sx={{ color: '#ff0000' }}
        >
          {error || 'Categoría no encontrada'}
        </Typography>
      </Box>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Typography 
          variant="h3" 
          component="h1" 
          className="mb-8 text-center font-orbitron"
          sx={{
            color: '#00ff00',
            textShadow: '0 0 10px rgba(0, 255, 0, 0.3)',
            fontFamily: 'Orbitron, sans-serif',
            fontWeight: 700,
          }}
        >
          {mainCategory.name}
        </Typography>

        {subCategories.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Grid container spacing={2}>
              {subCategories.map((subCategory) => (
                <Grid item xs={12} md={3} key={subCategory.id}>
                  <Card
                    onClick={() => setSelectedCategory(subCategory.id)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      textAlign: 'center',
                      bgcolor: selectedCategory === subCategory.id ? 'rgba(0, 255, 0, 0.2)' : 'black',
                      border: '1px solid #00ff00',
                      color: '#00ff00',
                      '&:hover': {
                        bgcolor: 'rgba(0, 255, 0, 0.1)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Typography variant="h6">
                      {subCategory.name}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {selectedCategory && (
          <ProductGrid 
            categoryId={selectedCategory}
            mainCategoryId={mainCategory.id}
          />
        )}
      </div>
    </div>
  );
};

export default CategoryPage;